import { SurveyListOption } from '@hyper-inferno/survey-elements-core';
import { ITransformationContext } from '../survey-engine/survey-engine.class';
import { cleanupSandbox, flattenResponses } from './piping-utils';
import { SurveyBundle } from '../survey-bundle/survey-bundle.class';
import { SurveySession } from '../survey-session/survey-session.class';

export interface CustomScriptContext {
  bundle: SurveyBundle;
  session: SurveySession;
  handlers: Record<string, any>;
  ctx: ITransformationContext;
}

export class CustomScriptEngine {
  /**
   * Executes arbitrary JavaScript/TypeScript string instructions safely in a sandbox environment.
   */
  public static execute(
    code: string,
    currentOptions: number[],
    context: CustomScriptContext,
  ): number[] {
    if (!code || !code.trim()) {
      return [...currentOptions];
    }

    // Clone working options list to preserve immutability until execution finishes
    let optionsList = [...currentOptions];

    const scope = this.createExecutionScope(
      () => optionsList,
      (updatedList: number[]) => {
        optionsList = [...updatedList];
      },
      context,
    );

    try {
      this.runInSandbox(code, scope);
    } catch (err) {
      console.error('Custom Script Execution Failed:', err);
    }

    return [...optionsList];
  }

  /**
   * Creates the execution context and provides helper methods for list modification inside script scope.
   */
  private static createExecutionScope(
    getOptions: () => number[],
    updateOptions: (list: number[]) => void,
    context: CustomScriptContext,
  ) {
    const { bundle, session, ctx } = context;

    /**
     * Retrieve options by list name.
     */
    const getListOptionsByName = (
      listName: string,
    ): SurveyListOption[] => {
      const list = bundle.getListByListName?.(listName);

      if (!list) {
        throw new Error(`List not found: ${listName}`);
      }

      const options =
        ctx.listBuilding?.getOptionsByListId(
          list.id,
          ctx,
          false,
        ) ?? [];

      return options;
    };

    // ADD
    const _ADD = (
      sourceOptions: SurveyListOption[],
      startMember?: number,
      endMember?: number,
    ): void => {
      const currentOptions = getOptions();

      // Extract option keys, accommodating either property naming convention.
      const sourceKeys = sourceOptions.map((option: any) =>
        Number(option.option_key ?? option.optionKey),
      );

      if (
        sourceKeys.some(
          (key) => !Number.isFinite(key),
        )
      ) {
        return;
      }

      let selectedKeys: number[];

      if (
        startMember === undefined &&
        endMember === undefined
      ) {
        // ADD(ListName): all members
        selectedKeys = sourceKeys;
      } else if (
        startMember !== undefined &&
        endMember === undefined
      ) {
        // ADD(ListName, Member#): one-based member number
        selectedKeys =
          startMember >= 1 &&
            startMember <= sourceKeys.length
            ? [sourceKeys[startMember - 1]]
            : [];
      } else if (
        startMember !== undefined &&
        endMember !== undefined
      ) {
        // ADD(ListName, StartMember#, EndMember#)
        if (
          startMember < 1 ||
          endMember < startMember ||
          endMember > sourceKeys.length
        ) {
          return;
        }

        selectedKeys = sourceKeys.slice(
          startMember - 1,
          endMember,
        );
      } else {
        return;
      }

      const updatedOptions = [...currentOptions];

      for (const key of selectedKeys) {
        if (!updatedOptions.includes(key)) {
          updatedOptions.push(key);
        }
      }

      updateOptions(updatedOptions);
    };

    const ADD = (
      listName: string,
      startMember?: number,
      endMember?: number,
    ): void => {
      const sourceOptions =
        getListOptionsByName(listName);

      _ADD(
        sourceOptions,
        startMember,
        endMember,
      );
    };

    // REMOVE
    const _REMOVE = (
      sourceOptions: SurveyListOption[],
      startMember?: number,
      endMember?: number,
    ): void => {
      const currentOptions = getOptions();

      const sourceKeys = sourceOptions
        .map((option: any) =>
          Number(
            option.option_key ??
            option.optionKey,
          ),
        )
        .filter((key) =>
          Number.isFinite(key),
        );

      let removeKeys: number[];

      // REMOVE(ListName)
      // Remove all options from the source list
      if (
        startMember === undefined &&
        endMember === undefined
      ) {
        removeKeys = sourceKeys;
      }

      // REMOVE(ListName, Member#)
      // Old ListBuilding behavior:
      // Member# is treated as the actual option key
      else if (
        startMember !== undefined &&
        endMember === undefined
      ) {
        removeKeys = [startMember];
      }

      // REMOVE(ListName, StartMember#, EndMember#)
      // Old ListBuilding behavior:
      // Remove option keys within the numeric range
      else if (
        startMember !== undefined &&
        endMember !== undefined
      ) {
        if (startMember > endMember) {
          return;
        }

        removeKeys = sourceKeys.filter(
          (optionKey) =>
            optionKey >= startMember &&
            optionKey <= endMember,
        );
      } else {
        return;
      }

      const removeSet = new Set(
        removeKeys,
      );

      const updatedOptions =
        currentOptions.filter(
          (optionKey) =>
            !removeSet.has(
              Number(optionKey),
            ),
        );

      updateOptions(
        updatedOptions,
      );
    };

    const REMOVE = (
      listName: string,
      startMember?: number,
      endMember?: number,
    ): void => {
      const sourceOptions =
        getListOptionsByName(
          listName,
        );

      _REMOVE(
        sourceOptions,
        startMember,
        endMember,
      );
    };

    // AIC
    const AIC = (
      questionName: string,
      startMember?: number,
      endMember?: number,
    ): void => {
      const element =
        bundle.getElementByName(questionName);

      if (!element) {
        throw new Error(
          `Question not found: ${questionName}`,
        );
      }

      const answer =
        session.jsn_responses[element.id] ?? {};

      const handler = context.handlers[element.type];

      if (
        !handler ||
        typeof handler.getValue !== 'function'
      ) {
        throw new Error(
          `No getValue implementation for question: ${questionName}`,
        );
      }

      const selectedKeys: number[] =
        handler.getValue(element, answer) ?? [];

      const questionList =
        bundle.getListOfQuestion(element.id);

      const chosenOptions =
        (questionList?.options ?? []).filter(
          (option: any) =>
            selectedKeys.includes(
              Number(option.option_key),
            ),
        );

      _ADD(
        chosenOptions,
        startMember,
        endMember,
      );
    };

    // ANC
    const ANC = (
      questionName: string,
      startMember?: number,
      endMember?: number,
    ): void => {
      const element = bundle.getElementByName(questionName);

      if (!element) {
        throw new Error(`Question not found: ${questionName}`);
      }

      const answer = session.jsn_responses[element.id] ?? {};
      const handler = context.handlers[element.type];

      if (
        !handler ||
        typeof handler.getValue !== 'function'
      ) {
        throw new Error(
          `No getValue implementation for question: ${questionName}`,
        );
      }

      const selectedKeys: number[] =
        handler.getValue(element, answer) ?? [];

      const questionList = bundle.getListOfQuestion(element.id);
      const sourceOptions = questionList?.options ?? [];

      const notChosenOptions = sourceOptions.filter(
        (option: any) =>
          !selectedKeys.includes(Number(option.option_key)),
      );

      _ADD(notChosenOptions, startMember, endMember);
    };

    // RIC
    const RIC = (
      questionName: string,
      startMember?: number,
      endMember?: number,
    ): void => {
      const element = bundle.getElementByName(questionName);

      if (!element) {
        throw new Error(`Question not found: ${questionName}`);
      }

      const answer = session.jsn_responses[element.id] ?? {};
      const handler = context.handlers[element.type];

      if (
        !handler ||
        typeof handler.getValue !== 'function'
      ) {
        throw new Error(
          `No getValue implementation for question: ${questionName}`,
        );
      }

      const selectedKeys: number[] =
        handler.getValue(element, answer) ?? [];

      const questionList =
        bundle.getListOfQuestion(element.id);

      const chosenOptions =
        (questionList?.options ?? []).filter(
          (option: any) =>
            selectedKeys.includes(
              Number(option.option_key),
            ),
        );

      _REMOVE(
        chosenOptions,
        startMember,
        endMember,
      );
    };

    // LIST_MAX
    const LIST_MAX = (count: number): void => {
      if (!Number.isFinite(count)) {
        return;
      }

      const currentOptions = getOptions();

      updateOptions(
        currentOptions.slice(0, count),
      );
    };

    // SORT_BY_VALUE
    const SORT_BY_VALUE = (): void => {
      const currentOptions = getOptions();

      updateOptions(
        [...currentOptions].sort(
          (a, b) => Number(a) - Number(b),
        ),
      );
    };

    // LIST_LENGTH
    const LIST_LENGTH = (
      listName?: string,
    ): number => {
      if (!listName) {
        return getOptions().length;
      }

      return getListOptionsByName(listName).length;
    };

    // RANDOMIZE
    const RANDOMIZE = (
      startMember?: number,
      endMember?: number,
    ): void => {
      const options = [...getOptions()];

      if (options.length <= 1) {
        return;
      }

      // Members are 1-based.
      // RANDOMIZE() shuffles the entire list.
      // RANDOMIZE(undefined, 2, 4) shuffles members 2–4.

      const start =
        startMember !== undefined
          ? startMember - 1
          : 0;

      const end =
        endMember !== undefined
          ? Math.min(
            endMember - 1,
            options.length - 1,
          )
          : options.length - 1;

      if (
        !Number.isInteger(start) ||
        !Number.isInteger(end) ||
        start < 0 ||
        end >= options.length ||
        start >= end
      ) {

        return;
      }

      // Fisher-Yates shuffle.
      for (let i = end; i > start; i--) {
        const j =
          start +
          Math.floor(
            Math.random() *
            (i - start + 1),
          );

        [options[i], options[j]] = [
          options[j],
          options[i],
        ];
      }

      updateOptions(options);
    };

    // Standardized Helper API injected into the custom script scope
    const helpers = {
      // Direct access to current items
      get currentOptions() {
        return [...getOptions()];
      },

      addOption: (
        option_key: number,
        index?: number,
      ): void => {
        const currentOptions = getOptions();
        const updatedOptions = [...currentOptions];

        if (
          typeof index === 'number' &&
          index >= 0
        ) {
          updatedOptions.splice(
            index,
            0,
            option_key,
          );
        } else {
          updatedOptions.push(option_key);
        }

        updateOptions(updatedOptions);
      },

      // Fetch helper for external list items
      _ADD,
      ADD,
      AIC,
      ANC,
      RIC,
      LIST_MAX,
      SORT_BY_VALUE,
      LIST_LENGTH,
      RANDOMIZE,
      _REMOVE,
      REMOVE,
      getListOptionsByName,

      // Convenience Response map matching UniversalPiping format
      responses: cleanupSandbox(
        flattenResponses(
          session?.jsn_responses || {},
        ),
      ),

      // Direct references
      session,
      bundle,
      ctx,
      console,
      JSON,
      Math,
    };

    return helpers;
  }

  /**
   * Runs the JavaScript string inside a Proxy-isolated sandbox
   */
  private static runInSandbox(
    code: string,
    scope: Record<string, any>,
  ): any {
    // 1. Construct dynamic function using `with` block scope
    const fn = new Function(
      'scope',
      `
      with (scope) {
        return (() => {
          "use strict";
          ${code}
        })();
      }
    `,
    );

    // 2. Proxy handler intercepts all variable lookups inside the `with` block
    const proxy = new Proxy(scope, {
      get: (target, prop) => {
        if (prop in target) {
          return target[prop as any];
        }
        return undefined;
      },
      has: (_target, _prop) => {
        // Force all variable reads to go through the proxy scope first
        return true;
      },
    });

    return fn(proxy);
  }
}
